import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  MenuItem,
  TextField,
  Stack,
  SvgIcon,
  useTheme,
  useMediaQuery,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Plus } from '../../../assets';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { ConfirmationDeleteDialog } from '../../../components/shared/dialogs/ConfirmationDeleteDialog';
import { Section } from '../../../components/ui/Section';
import { Pill } from '../../../components/shared/Pill';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import CustomPagination from '../../../components/forms/Pagination';
import { TrainingBundleDto, TrainingBundleType, TrainingCategoryDto, trainingClient } from '../../../app/services/api/compliance/training';
import { UserContext } from '../../../components/shared/useUser';

const TrainingCoursesPacksPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation('training');
  const { hasPermissionTo } = useContext(UserContext);
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bundles, setBundles] = useState<TrainingBundleDto[]>([]);
  const [categories, setCategories] = useState<TrainingCategoryDto[]>([]);
  const [filterCategory, setFilterCategory] = useState<number>(0);
  const [filterType, setFilterType] = useState<TrainingBundleType | -1>(-1);
  const [filterStatus, setFilterStatus] = useState<number>(-1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bundleToDelete, setBundleToDelete] = useState<TrainingBundleDto | null>(null);
  const [cannotDeleteDialogOpen, setCannotDeleteDialogOpen] = useState(false);
  const [cannotDeleteReason, setCannotDeleteReason] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const rowsPerPage = 10;
  const canViewCoursesPacks = hasPermissionTo(['TrainingAdmin', 'TrainingContentManager', 'TrainingContentViewer']);
  const canManageCoursesPacks = hasPermissionTo(['TrainingAdmin', 'TrainingContentManager']);

  useEffect(() => {
    if (!canViewCoursesPacks) {
      setLoading(false);
      return;
    }
    loadData();
  }, [page, canViewCoursesPacks]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Show courses/packs across all franchises the user has access to, not just the
      // currently selected store's franchise (franchiseId omitted = all accessible franchises).
      const [categoriesData, bundlesData] = await Promise.all([
        trainingClient.getCategories(),
        trainingClient.getBundles(),
      ]);
      setBundles(bundlesData);
      setCategories(categoriesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('trainingCoursesPacks.failedToLoadData'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (bundle: TrainingBundleDto) => {
    setBundleToDelete(bundle);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bundleToDelete) return;

    try {
      setError(null);
      await trainingClient.deleteBundle(bundleToDelete.id);
      setDeleteDialogOpen(false);
      setBundleToDelete(null);
      await loadData();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('trainingCoursesPacks.failedToDeleteBundle');

      setDeleteDialogOpen(false);

      const isCertificateBlock = /certificate|cannot delete|has active/i.test(message);
      if (isCertificateBlock) {
        setCannotDeleteReason(message);
        setCannotDeleteDialogOpen(true);
        setBundleToDelete(null);
      } else {
        setError(message);
        setBundleToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBundleToDelete(null);
  };

  const filteredBundles = bundles.filter(bundle => {
    if (filterCategory && bundle.categoryId !== filterCategory) return false;
    if (filterType >= 0 && bundle.bundleType !== filterType) return false;
    if (filterStatus >= 0 && bundle.status !== filterStatus) return false;
    return true;
  });

  const maxPages = () => Math.ceil(filteredBundles.length / rowsPerPage);
  const paginatedBundles = filteredBundles.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  if (loading) {
    return (
      <PageArea>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <PageTitle subTitle={t('trainingCoursesPacks.subtitle')}>{t('trainingCoursesPacks.title')}</PageTitle>
        {canManageCoursesPacks && (
          <Button
            variant="primary"
            size="lg"
            startIcon={<SvgIcon><Plus /></SvgIcon>}
            onClick={() => navigate('/compliance/training/courses-packs/builder')}
          >
            {t('trainingCoursesPacks.newBundle')}
          </Button>
        )}
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Box
          display="flex"
          gap={2}
          flexWrap="wrap"
        >
          <TextField
            select
            label={t('trainingCoursesPacks.category')}
            value={filterCategory}
            onChange={(e) => {
              setFilterCategory(Number(e.target.value));
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value={0}>{t('trainingCoursesPacks.allCategories')}</MenuItem>
            {categories.map((cat) => (
              <MenuItem
                key={cat.id}
                value={cat.id}
              >
                {cat.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('trainingCoursesPacks.type')}
            value={filterType}
            onChange={(e) => {
              setFilterType(Number(e.target.value));
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value={-1}>{t('trainingCoursesPacks.allTypes')}</MenuItem>
            <MenuItem value={TrainingBundleType.Course}>{t('trainingCoursesPacks.course')}</MenuItem>
            <MenuItem value={TrainingBundleType.Pack}>{t('trainingCoursesPacks.pack')}</MenuItem>
          </TextField>
          <TextField
            select
            label={t('trainingCoursesPacks.status')}
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(Number(e.target.value));
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value={-1}>{t('trainingCoursesPacks.allStatuses')}</MenuItem>
            <MenuItem value={0}>{t('trainingCoursesPacks.draft')}</MenuItem>
            <MenuItem value={1}>{t('trainingCoursesPacks.published')}</MenuItem>
          </TextField>
        </Box>
      </Box>

      <Section>
        {filteredBundles.length === 0 ? (
          <EmptyTable
            bodyRowHeight={40}
            headerHeight={45}
            text={t('trainingCoursesPacks.emptyText')}
            searchTerm={''}
            onClear={() => {
              setFilterCategory(0);
              setFilterType(-1);
              setFilterStatus(-1);
            }}
            onAdd={() => {
              if (canManageCoursesPacks) {
                navigate('/compliance/training/courses-packs/builder');
              }
            }}
            onAddIsCreating={false}
            addItemText={t('trainingCoursesPacks.newBundle')}
            onAddIsDisabled={!canManageCoursesPacks}
            withPaginationShown={false}
          />
        ) : (
          <>
            <Box
              sx={{
                p: 5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="textMD"
                  fontWeight={600}
                >
                  {t('trainingCoursesPacks.title')}
                </Typography>
                <Pill text={`${filteredBundles.length} ${filteredBundles.length === 1 ? t('trainingCoursesPacks.bundle') : t('trainingCoursesPacks.bundles')}`} />
              </Box>
            </Box>

            <TableContainer sx={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
              <Table sx={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('trainingCoursesPacks.name')}</TableCell>
                    <TableCell>{t('trainingCoursesPacks.category')}</TableCell>
                    <TableCell>{t('trainingCoursesPacks.type')}</TableCell>
                    <TableCell>{t('trainingCoursesPacks.modules')}</TableCell>
                    <TableCell>{t('trainingCoursesPacks.franchises')}</TableCell>
                    <TableCell align="center">{t('trainingCoursesPacks.status')}</TableCell>
                    <TableCell align="right">{t('trainingCoursesPacks.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedBundles.map((bundle) => (
                    <TableRow
                      key={bundle.id}
                      hover
                      onClick={() => {
                        if (canManageCoursesPacks) {
                          navigate(`/compliance/training/courses-packs/builder/${bundle.id}`);
                        }
                      }}
                      sx={{
                        cursor: canManageCoursesPacks ? 'pointer' : 'default',
                        '&:hover': {
                          backgroundColor: theme.palette.custom.gray[50],
                        },
                      }}
                    >
                      <TableCell>
                        <Tooltip
                          title={bundle.name}
                          placement="top"
                        >
                          <Typography
                            variant="textSM"
                            fontWeight={500}
                            sx={{
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {bundle.name}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="textSM">
                          {bundle.categoryName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={bundle.bundleType === TrainingBundleType.Course ? t('trainingCoursesPacks.course') : t('trainingCoursesPacks.pack')}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="textSM">
                          {bundle.modules.length}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="textSM">
                          {bundle.franchiseIds.length}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={bundle.status === 1 ? t('trainingCoursesPacks.published') : t('trainingCoursesPacks.draft')}
                          color={bundle.status === 1 ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {canManageCoursesPacks && (
                          <>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/compliance/training/courses-packs/builder/${bundle.id}`);
                              }}
                              title={t('trainingCoursesPacks.edit')}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(bundle);
                              }}
                              color="error"
                              title={t('trainingCoursesPacks.delete')}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages()}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </>
        )}
      </Section>

      <ConfirmationDeleteDialog
        open={deleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        nameOfObject={bundleToDelete?.name ?? ''}
      />

      {/* Cannot Delete Warning Dialog – shown when bundle has issued certificates */}
      <Dialog
        open={cannotDeleteDialogOpen}
        onClose={() => setCannotDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          {t('trainingCoursesPacks.cannotDeleteTitle')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning"
            sx={{ mb: 2 }}>
            {t(
              'trainingCoursesPacks.cannotDeleteHasCertificates',
            )}
          </Alert>
          {cannotDeleteReason && (
            <Typography
              sx={{ color: 'text.secondary', mt: 1 }}>
              {t('trainingCoursesPacks.apiError')}: {cannotDeleteReason}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="primary"
            size="lg"
            onClick={() => setCannotDeleteDialogOpen(false)}
          >
            {t('trainingCoursesPacks.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default TrainingCoursesPacksPage;
